import React, { useState, useEffect, useRef, useContext } from "react";
import { Button, Box, Typography, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import { WidgetContext } from "src/contexts/WidgetContext";
import { useAuth0 } from "@auth0/auth0-react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import CreditsValueButtons from "../CreditsUi/CreditsValueButtons";
import {
  buttonContentIconSlotSx,
  buttonContentInnerSx,
  buttonContentLabelSx,
  buttonContentRowCenteredSx,
  buttonContentRowGridSx,
  buttonContentSectionTitleSx,
} from "../AnimatedButton/buttonContentLayout";

interface ChatBotButtonProps {
  icon: string | React.ReactNode;
  label: string;
  onClick: () => void;
  isLoadingStart?: boolean;
  isLoadingNews?: boolean;
  isLoadingRankings?: boolean;
  isLoadingWaiver?: boolean;
  isStreamActive?: boolean;
  isLoadingRosters?: boolean;
  isLoadingRundown?: boolean;
  hasLoadingStarted?: boolean;
  selectedPartner: string;
  signal?: AbortSignal;
  startData?: any;
  newsData?: any;
  rankingsData?: any;
  waiverData?: any;
  rostersData?: any;
  rundownData?: any;
  onRetry?: () => void;
  hasError?: boolean;
  commonLeagueDataFailed?: boolean;
  returnedFromBackground?: boolean;
  selectedTeamInfo?: { platformId: string; teamName: string; leagueId: string };
  waiverStreamState?: { status: string; thought: string; isStreamActive: boolean; teamKey: string | null };
  startStreamState?: { status: string; thought: string; isStreamActive: boolean; teamKey: string | null };
  rundownStreamState?: { status: string; thought: string; isStreamActive: boolean; teamKey: string | null };
  isAnalysisPending?: boolean;
  isReadyForManualTrigger?: boolean;
  estimatedTimes?: { waiver: number; start: number; rundown: number; news: number; rankings: number; rosters: number; chatbot?: number } | null;
  // NEW: Optional manual progress target (0-100) for stream-driven progress
  manualProgressTarget?: number;
  isCreditsAnimating?: boolean;
  creditsBoxRef?: React.RefObject<HTMLDivElement>;
  userSubscription?: string;
  /** When false (backend disabled), button stays visible but is not clickable and label shows "{label} Coming this Fall". */
  endpointEnabled?: boolean;
  /** Un-Synced: blocked until a real fantasy league is synced (same as AnimatedButton). */
  requiresSyncedLeague?: boolean;
}

const ChatBotButton: React.FC<ChatBotButtonProps> = ({
  icon,
  label,
  onClick,
  isLoadingStart,
  isLoadingNews,
  isLoadingRankings,
  isLoadingWaiver,
  isLoadingRosters,
  isLoadingRundown,
  hasLoadingStarted = false,
  selectedPartner,
  startData,
  waiverData,
  rundownData,
  onRetry,
  hasError,
  commonLeagueDataFailed,
  returnedFromBackground,
  selectedTeamInfo,
  isAnalysisPending,
  isReadyForManualTrigger,
  estimatedTimes,
  manualProgressTarget,
  isCreditsAnimating,
  creditsBoxRef,
  userSubscription,
  endpointEnabled,
  requiresSyncedLeague = false,
}) => {
  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);

  const endpointAvailable = endpointEnabled !== false;
  const displayLabel = requiresSyncedLeague
    ? `${label}\nSync A League`
    : !endpointAvailable
      ? `Chat Coming Soon`
      : label;
  // States to handle the progress of the animation and the completion status
  // const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  // Refs to handle animation and start time
  const animationFrameRef = useRef<number>();
  const startTimeRef = useRef<number>(Date.now());
  const [showRetry, setShowRetry] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);
  const isManualLabel = ["Waivers", "Start/Sit", "Analysis", "Chatbot"].includes(label);

  const isMobile = isMobileWebBrowser() || mobile;
  // Get authId from user auth0
  const { user } = useAuth0();
  const authId = user?.sub;

  const [isIconHovered, setIsIconHovered] = useState(false);

  // Generate unique key based on team/platform/section
  const progressKey = `${authId}-${selectedTeamInfo?.platformId}-${selectedTeamInfo?.leagueId}-${label.toLowerCase()}`;

  const { getButtonProgress, updateButtonProgress } = useContext(WidgetContext);
  // Get persisted progress from the context
  const persistedProgress = getButtonProgress(progressKey);

  // CRITICAL: For manual buttons, always start at 0% when component mounts
  // This ensures buttons don't show 100% when switching teams with cached data
  const initialProgress = isManualLabel ? 0 : persistedProgress;
  const [progress, setProgress] = useState(initialProgress);

  // Update context when progress changes
  useEffect(() => {
    updateButtonProgress(progressKey, progress);
  }, [progress, progressKey]);

  // A permanent error is one that should not be retried.
  // This includes the global "common data failed" and the specific "no draft" error.
  const isPermanentError = !!commonLeagueDataFailed;
  // Un-Synced blocked: same visual/disabled treatment as permanent error, without that error path
  const isBlockedForUnsynced = requiresSyncedLeague;
  // Chat is not treated as a permanent-error surface for commonLeagueDataFailed (user can still open chat).
  // Readiness is still enforced via hasLoadingStarted / isAnalysisPending / hasError below.
  // const isPermanentError = false;

  // DEBUG: Log when permanent error should be true
  // if (commonLeagueDataFailed) {
  //   console.log(`[ChatBotButton] isPermanentError:`, isPermanentError, "commonLeagueDataFailed:", commonLeagueDataFailed);
  // }

  const initialColor = "#111111";
  const finalColor = "#222222";

  // Set the estimated times for each endpoint (in milliseconds)
  // Use times from backend if available, otherwise use default values
  const defaultEstimatedTimes = {
    rosters: 17000, // 17 s (before 13.2 s) 
    chatbot: 22000, // 22 s (before 30 s)
  };

  // Variable that indicates if any operation is in the process of being loaded
  // Variable that indicates if any operation is in the process of being loaded
  const currentLoading = isLoadingStart || isLoadingNews || isLoadingRankings || isLoadingWaiver || isLoadingRosters || isLoadingRundown;

  // Team key actual (plataforma + league) para este botón
  const currentTeamKey = selectedTeamInfo ? `${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}` : null;

  // Button disabled until the loading is complete (matching Trades logic)
  const isDisabled =
    !endpointAvailable ||
    isPermanentError || // Disabled for permanent errors
    isBlockedForUnsynced ||
    !hasLoadingStarted ||
    isAnalysisPending; // ChatBot disabled while super analysis is running (same as Trades)

  const shouldBeWhite =
    endpointAvailable &&
    !isPermanentError &&
    !isBlockedForUnsynced &&
    !hasError && // This is the main fix. An errored button should never be styled as "complete".
    ((label === "Chatbot" && isCompleted && isReadyForManualTrigger) || // ChatBot: white when super analysis complete (same as Trades)
      (isCompleted && !currentLoading)); // Other buttons: original logic

  // CRITICAL: Calculate backgroundColor - when isPermanentError is true, use finalColor (gray)
  const backgroundColor = isPermanentError || isBlockedForUnsynced ? finalColor : shouldBeWhite ? finalColor : initialColor;
  // Figma-style: not ready = dark gradient fading to page bg (#000000); ready/100% = lighter gradient
  const gradientTopColor = backgroundColor === initialColor ? "#1f1f1f" : "#555555";
  const gradientBottomColor = backgroundColor === initialColor ? "#111111" : "#333333";
  // Same gradient as "ready" state so the progress bar has gradient and matches at 100%
  const loaderGradient = "linear-gradient(180deg, #555555, #111111)";
  // Ref to track previous progressKey for team change detection
  const prevProgressKeyRef = useRef(progressKey);

  // CRITICAL: Always reset progress to 0 when team changes (progressKey changes)
  // This prevents showing progress from previous team (copied from AnimatedButton Trades logic)
  useEffect(() => {
    if (progressKey !== prevProgressKeyRef.current) {
      setProgress(0);
      setIsCompleted(false);
      startTimeRef.current = Date.now();
      // Clear persisted progress for the old key
      updateButtonProgress(prevProgressKeyRef.current, 0);
      prevProgressKeyRef.current = progressKey;
    }
  }, [progressKey, updateButtonProgress]);

  // REMOVED: Do NOT reset progress when isAnalysisPending (Trades behavior)
  // ChatBot should maintain progress during analysis like Trades, not reset like Waivers
  // useEffect(() => {
  //   const isManualLabel = ["Waivers", "Start/Sit", "Analysis", "Chatbot"].includes(label);
  //   if (isManualLabel && isAnalysisPending) {
  //     setProgress(0);
  //     setIsCompleted(false);
  //   }
  // }, [isAnalysisPending, label]);

  // useEffect to handle the loading animation (copied from AnimatedButton for Trades behavior)
  useEffect(() => {
    if (commonLeagueDataFailed) {
      setProgress(100);
      setIsCompleted(true);
      return;
    }

    // ChatBot: when analysis is skipped or already complete (e.g. Un-Synced), snap to ready visuals.
    // Without this, isAnalysisPending=false with progress=0 never reaches the lighter ready gradient.
    if (
      label === "Chatbot" &&
      !isAnalysisPending &&
      isReadyForManualTrigger &&
      hasLoadingStarted
    ) {
      if (progress < 100 || !isCompleted) {
        setProgress(100);
        setIsCompleted(true);
      }
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }

    // Use times from backend if available, otherwise use default values
    const timesToUse = estimatedTimes || defaultEstimatedTimes;
    const estimatedDuration = timesToUse.chatbot ?? defaultEstimatedTimes.chatbot;

    // Progress bar follows super analysis only (Chat is independent of roster fetch; rosters drive Trades, not Chat).
    const loadingForProgress = isAnalysisPending;

    if (loadingForProgress) {
      // Restart bar when super analysis becomes pending (team switch resets via progressKey effect).
      setProgress(0);
      setIsCompleted(false);
      startTimeRef.current = Date.now();
      updateButtonProgress(progressKey, 0);

      // Animation function to be executed at every frame
      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTimeRef.current;

        const maxProgress = 95;
        const nextProgress = Math.min((elapsed / estimatedDuration) * 100, maxProgress);

        setProgress(nextProgress);
        if (loadingForProgress) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (!loadingForProgress && progress > 0) {
      // For ChatBot, only complete to 100% when super analysis is complete (same as Trades)
      if (isReadyForManualTrigger) {
        setProgress(100);
        setIsCompleted(true);
      } else {
        // Keep progress at 95% max until super analysis completes
        setProgress(Math.min(progress, 95));
        setIsCompleted(false);
      }
    }

    // Cleaning useEffect: cancel the animation when the component is dismounted or loading state changes
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isAnalysisPending, commonLeagueDataFailed, isReadyForManualTrigger, progressKey, hasLoadingStarted, label]);

  // Modify the logic of when a button is completed (matching Trades logic)
  useEffect(() => {
    // For ChatBot, only mark as completed when super analysis is complete (same as Trades)
    if (label === "Chatbot") {
      // Do not gate Chat completion on roster/news/waiver loading — Chat is independent of those pipelines.
      if (isReadyForManualTrigger && hasLoadingStarted) {
        const timer = setTimeout(() => {
          setIsCompleted(true);
        }, 500); // Small delay for the transition

        return () => clearTimeout(timer);
      } else {
        setIsCompleted(false);
      }
    }
  }, [hasLoadingStarted, label, isReadyForManualTrigger]);

  //useEffect to handle the retry button
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (hasError) {
      timeoutId = setTimeout(() => {
        setShowRetry(true);
      }, 500);
    } else {
      setShowRetry(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasError]);

  // function to handle the click of the retry button
  const handleClick = () => {
    if (requiresSyncedLeague) return;
    if (hasError) {
      if (!hasRetried) {
        setHasRetried(true);
        setProgress(0); // Set progress to 0 localy
        updateButtonProgress(progressKey, 0); // Set progress to 0 in the widget context
        startTimeRef.current = Date.now(); // Set the start time to the current time
        onRetry?.();
      }
    } else {
      onClick?.();
    }
  };

  // Helper to render either an image icon (string) or a React icon component
  // const isManualLabel = ["Waivers", "Start/Sit", "Analysis"].includes(label);
  const manualLoading = label === "Waivers" ? isLoadingWaiver : label === "Start/Sit" ? isLoadingStart : label === "Analysis" ? isLoadingRundown : false;

  const isPendingState = isManualLabel ? isAnalysisPending : null;
  // NEW: Detect when button is actively loading
  // NEW: For manual buttons, consider "loading" only until first text_chunk arrives
  // This controls pointerEvents and icon/text color, NOT the progress bar logic.
  const isLoading = isManualLabel ? isAnalysisPending : isPendingState;

  // Render the icon
  const renderIcon = (withLargeSize: boolean) => {
    if (typeof icon === "string") {
      // Calculate opacity: 0.5 if analyzing (manual label), otherwise use existing logic
      const iconOpacity = !endpointAvailable
        ? 0.5 :
        isPendingState ? 0.5 : shouldBeWhite || !hasError ? 1 : 0.5;
      // Use brightness filter to darken white icons to gray when loading
      const iconFilter = !endpointAvailable
        ? `grayscale(100%) brightness(0.9)`
        : isLoading
          ? `grayscale(100%) brightness(0.8)` // Gray when loading (grayscale + darker)
          : isPermanentError || isBlockedForUnsynced
            ? `grayscale(100%) brightness(0.5)` // Gray when error (matches #808080)
            : `grayscale(${shouldBeWhite ? 0 : 100}%)`;

      const iconSize = withLargeSize ? (label === "Chatbot" ? "32px" : "40px") : "30px";

      return (
        <img
          src={icon}
          alt={label}
          width={iconSize}
          height={iconSize}
          color={PureLightThemeCaaS.colors.alpha.orange[100]}
          style={{
            color: PureLightThemeCaaS.colors.alpha.orange[100],
            // filter: `grayscale(${shouldBeWhite ? 0 : 100}%)`,
            filter: iconFilter,
            transition: "filter 0.8s",
            // opacity: shouldBeWhite || !hasError ? 1 : 0.5,
            opacity: iconOpacity,
            // opacity: shouldBeWhite ? 1 : 0.5,
          }}
        />
      );
    }

    if (React.isValidElement(icon)) {
      const iconColor =
        label === "Chatbot"
          ? isAnalysisPending
            ? "#a0a0a0" // Light gray when pending (match AnimatedButton isPendingState)
            : isPermanentError || isBlockedForUnsynced
              ? "#808080" // Gray when error / Un-Synced blocked
              : "#eaeaea" // White when ready
          : isPendingState
            ? "#a0a0a0" // Gray when loading
            : isPermanentError || isBlockedForUnsynced
              ? "#808080" // Gray when error
              : label === "Rosters"
                ? shouldBeWhite
                  ? "#eaeaea"
                  : "#eaeaea"
                : shouldBeWhite
                  ? "#eaeaea"
                  : "#eaeaea";
      // NEW: For ChatBot, adjust opacity based on pending state
      const iconOpacity =
        label === "Chatbot"
          ? isAnalysisPending
            ? 0.5
            : 1 // 0.5 when pending, 1 when ready
          : isPendingState
            ? 0.5
            : 1;
      const strokeColor = isIconHovered ? PureLightThemeCaaS.colors.alpha.orange[100] : "#eaeaea";
      return React.cloneElement(icon, {
        style: {
          fill: "none",
          stroke: iconColor,
          strokeWidth: 0.7,
          transition: "stroke 0.8s, opacity 0.8s",
          opacity: iconOpacity,
          fontSize: withLargeSize ? 45 : 30,
          // "&:hover": {
          //   stroke: PureLightThemeCaaS.colors.alpha.orange[100],
          // },
          ...(icon.props?.style || {}),
        },
        // onMouseEnter: () => setIsIconHovered(true),
        // onMouseLeave: () => setIsIconHovered(false),
      });
    }

    return null;
  };

  // Get the number of credits to display in the credits box for agent endpoints
  const chatBotCredits = frontEnvNumber("REACT_APP_CHATBOT_CREDITS", 10);
  const isAgentEndpoint = ["Waivers", "Start/Sit", "Team Analysis", "Draft Advice"].includes(label);
  const isChatBot = label === "Chatbot";
  const noCreditsInBox = isChatBot ? chatBotCredits : "FREE";


  return (
    <Button
      variant="contained"
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        // backgroundColor: backgroundColor,
        background: `linear-gradient(180deg, ${gradientTopColor}, ${gradientBottomColor})`,
        backgroundColor: "transparent",
        borderRadius: "8px",
        // border: "1px solid #bbbbbb",
        // transition: "background-color 0.3s",
        border: "none",
        // p: 0,
        transition: "background 0.3s",
        height: "70px",
        // pointerEvents: isLoading ? "none" : "auto",
        pointerEvents: "auto",

        // opacity: isDisabled ? 0.5 : 1,
        "&:hover": {
          // backgroundColor: backgroundColor,
          background: `linear-gradient(180deg, ${gradientTopColor}, ${gradientBottomColor})`,
          backgroundColor: "transparent",
          //this is to change the color of the icon when the button is hovered
          // "& svg *": {
          //   stroke: PureLightThemeCaaS.colors.alpha.orange[100],
          //   transition: "stroke 0s ease",
          // },
          // //this is to change the color of the text when the button is hovered
          // "& .MuiTypography-root": {
          //   color: PureLightThemeCaaS.colors.alpha.orange[100],
          //   transition: "color 0s ease",
          // },
        },
        // CRITICAL: Override Material-UI's default disabled styles so background stays black like AnimatedButton (no gray tint)
        "&.Mui-disabled": {
          backgroundColor: backgroundColor + " !important",
          opacity: 1,
        },
      }}
      onClick={handleClick}
      // Manual buttons:
      // - disabled while super analysis is running
      // - disabled while endpoint is starting (loading) until first text_chunk arrives (isGeneratingResult === true)
      // - enabled when ready for manual trigger or streaming has produced content
      disabled={
        // ChatBot behavior: same as Trades (disabled while analysis pending or has error)
        isPermanentError || isBlockedForUnsynced || isDisabled || isAnalysisPending || (hasError && hasRetried)
      }
    >
      {/* Icon and text */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          // ChatBot: show progress 0→90 during analysis; other manual buttons force 0 until their stream starts
          width: `${label === "Chatbot" ? (!endpointAvailable ? 0 : progress) : isManualLabel && isAnalysisPending ? 0 : progress}%`,
          background: loaderGradient,
          // borderRight: progress > 0 ? `1px solid #2a2a2a` : "none",
          zIndex: 1,
          // transition: isManualLabel && typeof manualProgressTarget === "number" ? "width 0.6s linear" : progress === 100 ? "width 0.2s ease-out" : "none",
          // When progress reaches 100%, fade the loader out quickly so the button gradient is fully visible.
          // opacity: progress >= 100 ? 0 : 1,
          transition:
            progress < 100
              ? isManualLabel && typeof manualProgressTarget === "number"
                ? "width 0.6s linear"
                : "none"
              : "opacity 0.15s ease-out",
        }}
      />
      <Box sx={isMobile ? buttonContentRowGridSx : buttonContentRowCenteredSx}>
        {isMobile ? (
          <Box sx={buttonContentInnerSx}>
            <Box sx={buttonContentIconSlotSx}>
              {renderIcon(label === "Start/Sit" || label === "Trades" || label === "Rosters" || label === "Chatbot")}
            </Box>
            <Typography
              sx={{
                ...buttonContentLabelSx,
                color:
                  label === "Chatbot"
                    ? !endpointAvailable
                      ? "#808080"
                      : isAnalysisPending
                        ? "#808080"
                        : isPermanentError || isBlockedForUnsynced
                          ? "#808080" // Gray when error / Un-Synced blocked
                          : "#ffffff" // Gray when pending, white when ready
                    : PureLightThemeCaaS.colors.alpha.white[100],
                transition: "color 0",
                lineHeight: 1.2,
                fontSize: "13px",
                // fontSize: displayLabel.includes("\n") ? "13px" : "13.5px",
                fontWeight: displayLabel.includes("\n") ? 400 : 700,
                whiteSpace: "pre-line",
              }}
            >
              {
                isPermanentError
                  ? label === "Chatbot"
                    ? (
                      <>
                        Chat Not
                        <br />
                        Available
                      </>
                    )
                    : `${label} Not Available`
                  : displayLabel
              }
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={buttonContentIconSlotSx}>
              {renderIcon(label === "Start/Sit" || label === "Trades" || label === "Rosters" || label === "Chatbot")}
            </Box>
            <Typography
              sx={{
                ...buttonContentLabelSx,
                flex: "none",
                color:
              label === "Chatbot"
                ? !endpointAvailable
                  ? "#808080"
                  : isAnalysisPending
                    ? "#808080"
                    : isPermanentError || isBlockedForUnsynced
                      ? "#808080" // Gray when error / Un-Synced blocked
                      : "#ffffff" // Gray when pending, white when ready
                : PureLightThemeCaaS.colors.alpha.white[100],
            // backgroundColor: PureLightThemeCaaS.colors.alpha.orange[100],
            // color: isManualLabel && isReadyForManualTrigger ? "#ffffff" : label === "Rosters" ? (shouldBeWhite ? "#eaeaea" : "#808080") : shouldBeWhite ? "#ffffff" : "#808080",
            transition: "color 0",
            // paddingBottom: label === "Start/Sit" || label === "Trades" || label === "Chatbot" ? "17px" : label === "Rosters" ? "4px" : "0",
            // marginTop: label === "Rosters" ? "-10px" : 0,
            ...buttonContentSectionTitleSx,
            whiteSpace: 'pre-line',
            // textAlign: "left",
          }}
        >
          {
            isPermanentError
              ? label === "Chatbot" ?
                isMobile ?
                  <>
                    Chat Not
                    <br />
                    Available
                  </>
                  : "Chat Not Available"
                : `${label} Not Available`
              : displayLabel
          }
            </Typography>
          </>
        )}
      </Box>
      {/* Bottom right: credits */}
      <Box sx={{ position: 'absolute', bottom: -3, left: 0, pointerEvents: 'auto', zIndex: 1 }}>
        <CreditsValueButtons credits={noCreditsInBox} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} isChatBot={isChatBot} />
      </Box>
    </Button>
  );
};

export default ChatBotButton;
